import { Injectable, inject } from '@angular/core';
import { RestService } from './rest.service';
import { RoutineSchedule, TOOL_BY_ID } from '../shell/shell.models';
import { SessionService } from './session.service';
import { readCache, writeCache } from './persistent-cache';

@Injectable({ providedIn: 'root' })
export class RoutineService {
  // Persisted (localStorage) so the home plan paints instantly on cold start;
  // see persistent-cache.ts. Hydrated on construction, revalidated in the bg.
  private cache: RoutineSchedule | null = null;
  private session = inject(SessionService);

  constructor(private rest: RestService) {
    this.cache = readCache<RoutineSchedule>('routine', this.uid());
  }

  private uid(): number | undefined {
    return this.session.session?.user?.id;
  }

  /** Synchronous read of the cached schedule (null when never loaded). Lets the
   *  home screen paint instantly on cold start before any await. */
  peek(): RoutineSchedule | null {
    return this.cache;
  }

  async getSchedule(force = false): Promise<RoutineSchedule> {
    if (this.cache && !force) return this.cache;
    const resp = await this.rest.request<{ data: RoutineSchedule }>({
      method: 'GET',
      path: 'app-routine',
      throwError: true,
    });
    this.cache = resp?.data || {};
    writeCache('routine', this.uid(), this.cache);
    return this.cache;
  }

  async saveSchedule(schedule: RoutineSchedule): Promise<RoutineSchedule> {
    const resp = await this.rest.request<{ data: RoutineSchedule }>({
      method: 'POST',
      path: 'app-routine/save',
      body: { schedule },
      throwError: true,
    });
    this.cache = resp?.data || schedule;
    writeCache('routine', this.uid(), this.cache);
    return this.cache;
  }

  // Today's plan = active tools matching current time-of-day + frequency.
  getTodayPlan(schedule: RoutineSchedule): string[] {
    const time = this.timeOfDay();
    const dow = new Date().getDay();
    const plan: string[] = [];
    Object.keys(schedule || {}).forEach((id) => {
      const cfg = schedule[id];
      if (!cfg || !cfg.active) return;
      if (cfg.time && cfg.time !== 'anytime' && cfg.time !== time) return;
      if (cfg.freq === 'weekly' && dow !== 0) return;
      if (cfg.freq === '2-3x' && dow % 2 !== 0) return;
      if (!TOOL_BY_ID[id]) return;
      plan.push(id);
    });
    return plan;
  }

  timeOfDay(): 'morning' | 'midday' | 'evening' {
    const h = new Date().getHours();
    return h < 12 ? 'morning' : h < 17 ? 'midday' : 'evening';
  }
}
